import { formatWithCommas } from '@/utils/numberHelpers'
import { Icon } from '@iconify/react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { TransactionAction, type PendingGiftcardSale } from '../../types/ApiTypes'
import { useApi } from '../hooks/useApi'
import { Badge, MonoUsername } from '../styles/TableStyle'
import {
   CloseBtn,
   Grid,
   H1,
   Label,
   Overlay,
   Panel,
   Section,
   SectionTitle,
   TitleRow,
   Value
} from '../styles/TransactionDetailStyle'
import ConfirmPinModal from './ConfirmPinModal'
import { RejectConfirmationModal } from './RejectConfirmationModal'

interface Props {
   open: boolean
   onClose: () => void
   sale?: PendingGiftcardSale | undefined
   onSuccess?: () => void
}


export default function GiftcardDetails({ open, onClose, sale, onSuccess }: Props) {
   const [showReject, setShowReject] = useState(false)
   const [showPin, setShowPin] = useState<[boolean, 'approve' | 'reject', string | null]>([false, 'approve', null])
   const [previewImage, setPreviewImage] = useState<string | null>(null)
   const [processing, setProcessing] = useState(false)
   const { transaction } = useApi()

   const formatDateFull = (dateStr: string): string => {
      const date = new Date(dateStr)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}, ${hours}:${minutes}`
   }

   const formatTransactionType = (type: string): string => {
      return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
   }

   useEffect(() => {
      function esc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
      if (open) window.addEventListener('keydown', esc)
      return () => window.removeEventListener('keydown', esc)
   }, [open, onClose])

   if (!open || !sale) return null

   return (
      <Overlay onClick={onClose}>
         <RejectConfirmationModal
            open={showReject}
            txnId={sale.transactionId}
            onClose={() => setShowReject(false)}
            onSubmit={(reason) => {
               setShowReject(false)
               setShowPin([true, 'reject', reason])
            }}
         />
         <ConfirmPinModal
            open={showPin[0]}
            onClose={() => setShowPin([false, 'approve', null])}
            onConfirm={async () => {
               if (!sale) return

               setProcessing(true)
               try {
                  const action = showPin[1]
                  const reason = showPin[2]

                  const res = await transaction.approveRejectGiftcardSale({
                     transactionId: sale.id,
                     action: action === 'approve' ? TransactionAction.APPROVE : TransactionAction.REJECT,
                     ...(reason && { rejectionReason: reason })
                  })

                  if (res.status === 'success') {
                     toast.success(`Giftcard sale ${action === 'approve' ? 'approved' : 'rejected'} successfully`)
                     onClose()
                     onSuccess?.()
                  } else {
                     toast.error(res.message || `Failed to ${action} giftcard sale`)
                  }
               } catch (error) {
                  toast.error(error instanceof Error ? error.message : 'An error occurred')
               } finally {
                  setProcessing(false)
                  setShowPin([false, 'approve', null])
               }
            }}
         />

         <Panel onClick={e => e.stopPropagation()}>
            <TitleRow>
               <H1>Transaction Details</H1>
               <CloseBtn aria-label='Close' onClick={onClose}><Icon icon='material-symbols:close' width={20} /></CloseBtn>
            </TitleRow>

            <div style={{ flex: 1, overflowY: 'auto', marginRight: -24, paddingRight: 24, marginTop: 25 }}>
               <Section>
                  <Grid>
                     <Label>Transaction ID</Label>
                     <Value>{sale.transactionId}</Value>
                     <Label>Transaction Type</Label>
                     <Value>{formatTransactionType(sale.type)}</Value>
                     <Label>Date</Label>
                     <Value>{formatDateFull(sale.date)}</Value>
                     <Label>Status</Label>
                     <Value><Badge variant='pending'>Pending</Badge></Value>
                  </Grid>
               </Section>

               <Section>
                  <SectionTitle>User info</SectionTitle>
                  <Grid>
                     <Label>Email address</Label>
                     <Value>{sale.emailAddress}</Value>
                     <Label>Username</Label>
                     <Value><MonoUsername>@{sale.username}</MonoUsername></Value>
                  </Grid>
               </Section>

               <Section>
                  <SectionTitle>Summary</SectionTitle>
                  <Grid>
                     <Label>Card brand</Label>
                     <Value>{sale.giftcardType}</Value>
                     <Label>Card type</Label>
                     <Value>{sale.cardType.charAt(0).toUpperCase() + sale.cardType.slice(1)}</Value>
                     <Label>Subcategory</Label>
                     <Value>{sale.subcategory}</Value>
                     <Label>Card value</Label>
                     <Value>${formatWithCommas(sale.amountInUsd)}</Value>
                     <Label>Rate</Label>
                     <Value>₦{formatWithCommas(sale.rate)}</Value>
                     <Label>Naira value</Label>
                     <Value>₦{formatWithCommas(sale.amountInNaira)}</Value>
                     {sale.ecode ? (
                        <>
                           <Label>Ecode</Label>
                           <Value>{sale.ecode}</Value>
                        </>
                     ) : null}
                  </Grid>
               </Section>
               {sale.cardImages && sale.cardImages.length > 0 && (
                  <Section>
                     <SectionTitle>Card images</SectionTitle>
                     <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {sale.cardImages.map((url, i) => (
                           <div key={i} style={{ width: 56, height: 56, borderRadius: 6, overflow: 'hidden', background: '#F5F5F6', position: 'relative', flexShrink: 0, cursor: 'pointer' }} onClick={() => setPreviewImage(url)}>
                              <img src={url} alt={`card-${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <div style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: 100, background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                 <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_890_8320)">
                                       <path d="M3.00033 5L0.666992 7.33333M0.666992 7.33333H2.61937M0.666992 7.33333V5.38097" stroke="#3F3F45" stroke-linecap="round" stroke-linejoin="round" />
                                       <path d="M5 2.99935L7.33333 0.666016M7.33333 0.666016H5.38097M7.33333 0.666016V2.6184" stroke="#3F3F45" stroke-linecap="round" stroke-linejoin="round" />
                                    </g>
                                    <defs>
                                       <clipPath id="clip0_890_8320">
                                          <rect width="8" height="8" fill="white" />
                                       </clipPath>
                                    </defs>
                                 </svg>

                              </div>
                           </div>
                        ))}
                     </div>
                  </Section>
               )}
            </div>

            <div style={{ display: 'flex', gap: 12, paddingTop: 24, borderTop: '1px solid #F5F5F6' }}>
               <button onClick={() => setShowReject(true)} disabled={processing} className='flex-1 h-[38px] rounded-full text-[13px] cursor-pointer font-medium tracking-tight bg-[#F5F5F6] text-[#3F3F45] hover:bg-[#EEEEEF] disabled:opacity-50 disabled:cursor-not-allowed'>Reject</button>
               <button onClick={() => setShowPin([true, 'approve', null])} disabled={processing} className='flex-1 h-[38px] rounded-full text-[13px] cursor-pointer font-medium tracking-tight bg-[#AF7AD5] text-white hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed'>Approve</button>
            </div>
         </Panel>

         {previewImage && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }}>
               <button onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: 28, width: 40, height: 40, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
               <img src={previewImage} alt="Preview" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
            </div>
         )}
      </Overlay>
   )
}
